import { Checkbox } from "@/components/ui/checkbox"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { AnswerTarget } from "@/types/questionnaire/models"
import { CheckedState } from "@radix-ui/react-checkbox"
import { useMemo } from "react"
import { OptionClearSelectionButton } from "../PdfAnswerItem/OptionClearSelectionButton"

interface CheckBoxInputOptionProps {
  answerTargets: AnswerTarget[]
  value: string
  onChange: (
    apiValue: string,
    excelCellValue: string,
    cellAddress: string,
  ) => void
  onClear: (cellAddress: string, answerTarget: AnswerTarget) => void
  hasError: boolean
  hasWarning: boolean
  responsePairId: string | null
}

export const CheckBoxInputOption: React.FC<CheckBoxInputOptionProps> = ({
  answerTargets,
  value,
  onChange,
  onClear,
  responsePairId,
}) => {
  const hasMultipleAnswerTargets = answerTargets.length > 1

  const checkedResponsesArray = useMemo(() => {
    let _checkedResponsesArray

    try {
      _checkedResponsesArray = value.length ? JSON.parse(value) : []
    } catch {
      _checkedResponsesArray = []
    }

    return _checkedResponsesArray
  }, [value])

  const handleDropDownChange = (
    optionValue: string,
    cellAddress: string,
    currentValueIndex: number,
  ) => {
    const newValue = [...checkedResponsesArray]
    newValue[currentValueIndex] = optionValue

    const newResponseString = JSON.stringify(newValue)
    onChange(newResponseString, optionValue, cellAddress)
  }

  const handleRadioChange = (
    cellAddress: string,
    answerTarget: AnswerTarget,
    checkState: CheckedState,
  ) => {
    if (checkState === "indeterminate") {
      return
    }

    let newValue = [...checkedResponsesArray]
    const selectedLabel = answerTarget.label
    const indexOfSelectedLabel = newValue.indexOf(selectedLabel)
    const isPresentInCurrentArray = indexOfSelectedLabel > -1

    const positiveValueForExcel = answerTarget.checkedValue || "X"
    const negativeValueForExcel = answerTarget.uncheckedValue || ""

    const isChecked = Boolean(checkState)

    if (isChecked) {
      if (!isPresentInCurrentArray) {
        newValue.push(selectedLabel)
      }
      const newResponseString = JSON.stringify(newValue)
      onChange(newResponseString, positiveValueForExcel, cellAddress)
    }

    if (!isChecked) {
      newValue = newValue.filter((label) => label !== selectedLabel)
      const newResponseString = JSON.stringify(newValue)
      onChange(newResponseString, negativeValueForExcel, cellAddress)
    }
  }

  return (
    <>
      {answerTargets.map((answerTarget, answerTargetIndex) => {
        if (!answerTarget) {
          return null
        }

        const cellAddress = answerTarget.addr
        const isChecked = checkedResponsesArray.includes(answerTarget.label)

        return (
          <div
            className="space-y-S"
            key={`checkbox-section-${answerTarget.addr}`}
          >
            {answerTarget.semantics === "CheckboxOption" ? (
              <div className="border-gray-300 table w-full table-fixed">
                <div className="table-row">
                  <div className="border-gray-300 table-cell w-1/2 border p-2">
                    <Label htmlFor={`yes-${responsePairId}`}>
                      {answerTarget.label}
                    </Label>
                  </div>
                  <div className="border-gray-300 table-cell w-1/2 border p-2">
                    <div className="flex items-center justify-between">
                      <Checkbox
                        id={`yes-${responsePairId}`}
                        checked={isChecked}
                        onCheckedChange={(isChecked) => {
                          handleRadioChange(
                            cellAddress,
                            answerTarget,
                            isChecked,
                          )
                        }}
                      />

                      {isChecked && (
                        <OptionClearSelectionButton
                          onClear={() => {
                            onClear(cellAddress, answerTarget)
                          }}
                        />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ) : null}

            {answerTarget.semantics === "Choice" ? (
              <>
                {hasMultipleAnswerTargets ? (
                  <h5 className="mt-4">{answerTarget.label}</h5>
                ) : null}

                <Select
                  value={checkedResponsesArray[answerTargetIndex] ?? ""}
                  onValueChange={(value) => {
                    handleDropDownChange(
                      value,
                      answerTarget.addr,
                      answerTargetIndex,
                    )
                  }}
                >
                  <SelectTrigger className="w-[180px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      {answerTarget.allowedValues
                        ?.filter((v) => v !== "")
                        .map((optionValue, optionIndex) => {
                          return (
                            <SelectItem
                              value={optionValue}
                              key={`${answerTarget.addr}-${optionIndex}`}
                            >
                              {optionValue}
                            </SelectItem>
                          )
                        })}
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </>
            ) : null}
          </div>
        )
      })}
    </>
  )
}
